import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Container, Grid, Theme, Typography } from '@material-ui/core';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';
import { createStyles, makeStyles } from '@material-ui/styles';
import { IdCheckData, OcrMatchData } from 'api/file/interfaces/get-file-order-insights';
import clsx from 'clsx';
import React from 'react';

type BarcodeScanInfoProps = {
  barcodeScan: IdCheckData;
  phoneNumber: string;
  ocrMatchData: OcrMatchData;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    fullWidth: {
      width: '100%',
    },
    names: {
      fontSize: pxToRem(10),
      fontWeight: 500,
      paddingLeft: theme.spacing(1),
    },
    values: {
      fontSize: pxToRem(10),
      fontWeight: 350,
      paddingLeft: theme.spacing(1),
    },
    yellowRow: {
      backgroundColor: '#FFF3E0',
    },
    statusMessage: {
      padding: theme.spacing(0.75),
      borderRadius: 2,
      gap: theme.spacing(0.5),
      display: 'flex',
      alignItems: 'center',
    },
    statusMessageText: {
      fontWeight: 400,
      fontSize: pxToRem(10),
      color: '#663C00',
    },
    icon: {
      width: 10,
      height: 10,
    },
    warningIcon: {
      color: theme.palette.warning.dark,
    },
    flexRow: {
      display: 'flex',
      gap: theme.spacing(1),
    },
    summaryMsg: {
      paddingTop: theme.spacing(1.25),
      width: '100%',
    },
  }),
);

type OcrMatchDataStatusKey = keyof OcrMatchData;

const barcodeFields: {
  label: string;
  key: string;
  position?: 'left' | 'right';
  status?: OcrMatchDataStatusKey | undefined;
}[] = [
  { label: 'Phone Number', key: 'phoneNumber', position: 'left' },
  { label: 'ID Number', key: 'dLidNumberRaw', position: 'right' },
  { label: 'Issuer', key: 'issuingJurisdictionCvt', position: 'left' },
  { label: 'ID Number Formatted', key: 'dLidNumberFormatted', position: 'right' },
  { label: 'Name', key: 'name', status: 'isIssuerNameMatch', position: 'left' },
  { label: 'Gender', key: 'gender', status: 'isSexMatch', position: 'right' },
  { label: 'Address', key: 'address', status: 'isAddressMatch', position: 'left' },
  { label: 'Drive Class', key: 'driverClass', status: 'isDlClassMatch', position: 'right' },
  { label: 'Height (feet & inches)', key: 'heightFeetInches', status: 'isHeightMatch', position: 'right' },
  { label: 'Date of Birth', key: 'dateOfBirth', status: 'isDobMatch', position: 'left' },
  { label: 'Is Duplicate', key: 'isDuplicate', position: 'right' },
  { label: 'Expiration Date', key: 'expirationDate', status: 'isExpirationDateMatch', position: 'left' },
  { label: 'Doc Type', key: 'docType', position: 'right' },
  { label: 'Issue Date', key: 'issueDate', status: 'isIssueDateMatch', position: 'left' },
  { label: 'Doc Category', key: 'docCategory', position: 'right' },
];

const BarcodeField: React.FC<{
  label: string;
  value: string | null;
  status: string;
  classes: Record<string, string>;
}> = ({ label, value, status, classes }) => (
  <Grid container>
    <Grid item xs={6} className={clsx(status !== 'passed' ? classes.yellowRow : '', classes.flexRow)}>
      <Typography className={classes.names}>
        {label}
        {label !== '' && ':'}
      </Typography>
    </Grid>
    <Grid item xs={6} className={clsx(status !== 'passed' ? classes.yellowRow : '', classes.flexRow)}>
      <Typography className={classes.values}>{value}</Typography>
    </Grid>
  </Grid>
);

const getConcatenatedValue = (key: string, barcodeScan: any) => {
  if (key === 'name') {
    const { firstName = '', middleName = '', lastName = '' } = barcodeScan;
    return [firstName, middleName, lastName].filter(Boolean).join(' ').trim();
  }
  if (key === 'address') {
    const { address1 = '', address2 = '', city = '', state = '', postalCode = '' } = barcodeScan;
    return [address1, address2, city, state, postalCode].filter(Boolean).join(', ').trim();
  }
  return barcodeScan[key] || '';
};

const BarcodeScanInfo: React.FC<BarcodeScanInfoProps> = ({ barcodeScan, phoneNumber, ocrMatchData }) => {
  const classes = useStyles();

  const mergedBarcodeScanDetails = { ...barcodeScan, phoneNumber };

  // Check if any value in the barcodeScan is not "passed"
  // const hasNonPassedValues = Object.values(barcodeScan).some((value: any) => value.status !== 'passed');
  const hasNonPassedValues = barcodeFields.some(
    (field) => field.status && typeof ocrMatchData[field.status] === 'boolean' && ocrMatchData[field.status] === false,
  );

  return (
    <Container maxWidth={false}>
      <Grid container spacing={1}>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            {barcodeFields
              .filter((field) => field.position === 'left')
              .map((field) => (
                <BarcodeField
                  key={field.key}
                  label={field.label}
                  value={getConcatenatedValue(field.key, mergedBarcodeScanDetails)}
                  status={field.status ? (ocrMatchData[field?.status] ? 'passed' : '') : 'passed'}
                  classes={classes}
                />
              ))}
          </Grid>
          <Grid item xs={6}>
            {barcodeFields
              .filter((field) => field.position === 'right')
              .map((field) => (
                <BarcodeField
                  key={field.key}
                  label={field.label}
                  value={getConcatenatedValue(field.key, mergedBarcodeScanDetails)}
                  status={field.status ? (ocrMatchData[field?.status] ? 'passed' : '') : 'passed'}
                  classes={classes}
                />
              ))}
          </Grid>
        </Grid>

        {hasNonPassedValues && (
          <Grid container className={classes.summaryMsg}>
            <Grid item xs={12} className={clsx(classes.statusMessage, classes.yellowRow)}>
              <ReportProblemIcon className={clsx(classes.icon, classes.warningIcon)} />
              <Typography className={classes.statusMessageText}>
                Highlighted fields indicate differences between the barcode on the back of the ID and the information on
                the front.
              </Typography>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default BarcodeScanInfo;
